/**
 * Drawing Interactive Handles, Hit Testing & Drag State Snapshots.
 *
 * Computes on-screen coordinate positions for multi-point drawing handles (p1, p2, p3),
 * performs handle proximity hit-testing, and creates state snapshots before dragging.
 */

export const HIGHLIGHT_STYLES = {
  selectedColor: "#2196F3",
  hoverColor: "#4DA3E0",
  selectedAlpha: 0.3,
  hoverAlpha: 0.15,
  selectedRadius: 6,
  hoverRadius: 5,
  handleSize: 8,
  handleFill: "#2196F3",
  handleStroke: "#ffffff",
  handleLineWidth: 1.5,
} as const;

export interface Point2D {
  x: number;
  y: number;
}

export interface HandlePoint {
  handle: "p1" | "p2" | "p3";
  x: number;
  y: number;
}

export interface DrawingLike {
  id?: string;
  type?: string;
  p1?: { time: number; price: number };
  p2?: { time: number; price: number };
  p3?: { time: number; price: number };
  point1?: { time: number; price: number };
  point2?: { time: number; price: number };
  price?: number;
  /** Anchored VWAP only: the bar the curve starts from. It has no p1/p2/p3 — it is computed from the candles. */
  anchorTime?: number;
  locked?: boolean;
  [key: string]: unknown;
}

/**
 * Returns all active handle screen positions for a multi-point drawing.
 */
export function getHandlePoints(
  d: DrawingLike | null | undefined,
  coordToPixel: (time: number, price: number) => Point2D | null,
  /** Anchored VWAP only: the VWAP value at a bar time (the page owns the candles and volumes). */
  resolveAnchorPrice?: (anchorTime: number) => number | null,
): HandlePoint[] {
  if (!d || d.type === "hline") return [];

  // An anchored VWAP is a curve computed from the candles, so it stores only `anchorTime`. Its ONE handle sits at the
  // anchor bar and its VWAP there; dragging it moves the anchor (drag_engine). Nothing to show without a price.
  if (d.type === "avwap") {
    if (typeof d.anchorTime !== "number" || !resolveAnchorPrice) return [];
    const price = resolveAnchorPrice(d.anchorTime);
    if (price === null || price === undefined) return [];
    const p = coordToPixel(d.anchorTime, price);
    return p ? [{ handle: "p1", x: p.x, y: p.y }] : [];
  }

  const pts: HandlePoint[] = [];
  const a = d.p1 || d.point1;
  const b = d.p2 || d.point2;

  if (a) {
    const p = coordToPixel(a.time, a.price);
    if (p) pts.push({ handle: "p1", x: p.x, y: p.y });
  }
  if (b) {
    const p = coordToPixel(b.time, b.price);
    if (p) pts.push({ handle: "p2", x: p.x, y: p.y });
  }
  if (d.p3) {
    const p = coordToPixel(d.p3.time, d.p3.price);
    if (p) pts.push({ handle: "p3", x: p.x, y: p.y });
  }

  return pts;
}

/**
 * Finds if a mouse pixel coordinate is within tolerance of any handle point.
 */
export function findHandleNear(
  px: number,
  py: number,
  handles: HandlePoint[] | null | undefined,
  tolerance: number = 8,
): { handle: "p1" | "p2" | "p3" } | null {
  if (!handles || !handles.length) return null;

  for (const h of handles) {
    if (Math.abs(px - h.x) <= tolerance && Math.abs(py - h.y) <= tolerance) {
      return { handle: h.handle };
    }
  }

  return null;
}

/**
 * Creates an immutable snapshot of drawing coordinates for drag operations.
 */
export function createDrawingDragSnapshot(
  d: DrawingLike | null | undefined,
): Record<string, any> {
  const snapshot: Record<string, any> = {};
  if (!d) return snapshot;

  if (d.p1) snapshot.p1 = { time: d.p1.time, price: d.p1.price };
  if (d.p2) snapshot.p2 = { time: d.p2.time, price: d.p2.price };
  if (d.p3) snapshot.p3 = { time: d.p3.time, price: d.p3.price };
  if (d.point1) snapshot.p1 = { time: d.point1.time, price: d.point1.price };
  if (d.point2) snapshot.p2 = { time: d.point2.time, price: d.point2.price };
  if (d.price !== undefined) snapshot.price = d.price;
  if (d.type === "avwap" && typeof d.anchorTime === "number") snapshot.anchorTime = d.anchorTime;

  return snapshot;
}
